/**
 * The logged-in user's own social sharing preferences plus the
 * Full/Limited/Minimum level write.
 *
 * Web only ever reads its OWN preferences (never another user's), so this is
 * keyed by the logged-in `userId`. `useSetSharingLevel` optimistically
 * overwrites the managed and write-only keys and, on settle, invalidates the profile
 * surfaces whose visibility depends on them.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getSharingLevelTargets, type SharingLevel } from 'constants/socialSharingLevels';
import { createApiClient } from 'lib/api/createApiClient';
import { useAuthStore } from 'lib/stores/authStore';
import type { SocialPreferencesVo } from 'models';
import type { GetSocialPreferencesResponse } from 'models/reqres';
import config from 'utils/config';

import { perpsMarginTopHoldersKeys } from './perpsMarginTopHolders';
import { socialActivityKeys } from './socialActivity';
import { socialCategoryPnlKeys } from './socialCategoryPnl';
import { socialLeaderboardKeys } from './socialLeaderboard';
import { socialProfileKeys, socialProfileMetricsKeys } from './socialProfile';
import { socialProfileHoldingsKeys } from './socialProfileHoldings';
import { removeEmpty } from './utils';

const api = createApiClient({ baseURL: `${config.exchangeApiUrl}/v1` });

export const socialPreferencesKeys = {
    all: ['socialPreferences'] as const,
    byUser: (userId: string | null) => [...socialPreferencesKeys.all, removeEmpty({ userId })] as const,
};

/**
 * Query-options factory so both the `useSocialPreferences` hook and non-React
 * callers (e.g. the zustand social-onboarding store, via `getQueryClient`) read
 * the logged-in user's prefs through one query definition.
 */
export function socialPreferencesQueryOptions(userId: string | null) {
    return {
        queryKey: socialPreferencesKeys.byUser(userId),
        queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<SocialPreferencesVo> => {
            const res = await api.get<GetSocialPreferencesResponse>(`users/${userId}/social/sharing_preference`, {
                signal,
            });
            return res.data.preferences;
        },
        enabled: !!userId,
        staleTime: 60 * 1000,
    };
}

export function useSocialPreferences() {
    const userId = useAuthStore((s) => s.userId);
    return useQuery(socialPreferencesQueryOptions(userId));
}

/**
 * Plain (non-hook) per-key write, for callers outside React (the zustand
 * onboarding store). POSTs the same `set`/`unset` action as
 * `useSetSocialPreference`; the caller is responsible for invalidating
 * `socialPreferencesKeys` afterwards.
 */
export async function setSocialPreference(
    userId: string,
    option: keyof SocialPreferencesVo,
    value: boolean,
): Promise<void> {
    await api.post(`users/${userId}/social/sharing_preference`, {
        option,
        action: value ? 'set' : 'unset',
    });
}

interface SetSharingLevelContext {
    previous: SocialPreferencesVo | undefined;
}

export function useSetSharingLevel() {
    const queryClient = useQueryClient();
    const userId = useAuthStore((s) => s.userId);

    return useMutation<void, Error, { level: SharingLevel }, SetSharingLevelContext>({
        mutationFn: async ({ level }) => {
            if (!userId) {
                throw new Error('Cannot set sharing level when logged out');
            }
            // Full overwrite of the managed + write-only keys, sequential per-key POSTs.
            const targets = getSharingLevelTargets(level);
            for (const [option, value] of Object.entries(targets)) {
                await api.post(`users/${userId}/social/sharing_preference`, {
                    option,
                    action: value ? 'set' : 'unset',
                });
            }
        },
        onMutate: async ({ level }) => {
            const key = socialPreferencesKeys.byUser(userId);
            await queryClient.cancelQueries({ queryKey: key });
            const previous = queryClient.getQueryData<SocialPreferencesVo>(key);
            if (previous) {
                queryClient.setQueryData<SocialPreferencesVo>(key, {
                    ...previous,
                    ...getSharingLevelTargets(level),
                });
            }
            return { previous };
        },
        onError: (_err, _vars, context) => {
            if (context?.previous) {
                queryClient.setQueryData(socialPreferencesKeys.byUser(userId), context.previous);
            }
        },
        onSettled: () => {
            // Reconcile prefs + every surface whose visibility/data depends on them.
            void queryClient.invalidateQueries({ queryKey: socialPreferencesKeys.all });
            void queryClient.invalidateQueries({ queryKey: socialActivityKeys.all });
            void queryClient.invalidateQueries({ queryKey: socialProfileKeys.all });
            void queryClient.invalidateQueries({ queryKey: socialProfileMetricsKeys.all });
            void queryClient.invalidateQueries({ queryKey: socialProfileHoldingsKeys.all });
            void queryClient.invalidateQueries({ queryKey: socialCategoryPnlKeys.all });
            void queryClient.invalidateQueries({ queryKey: socialLeaderboardKeys.all });
            void queryClient.invalidateQueries({ queryKey: perpsMarginTopHoldersKeys.all });
        },
    });
}

interface SetSocialPreferenceContext {
    previous: SocialPreferencesVo | undefined;
}

/**
 * Toggle a single preference key (the per-metric write behind the leaderboard
 * join/leave buttons and onboarding). Optimistically flips the one key and, on
 * settle, invalidates prefs + activity (the level write's `useSetSharingLevel`
 * owns the nine-key group write).
 */
export function useSetSocialPreference() {
    const queryClient = useQueryClient();
    const userId = useAuthStore((s) => s.userId);

    return useMutation<void, Error, { option: keyof SocialPreferencesVo; value: boolean }, SetSocialPreferenceContext>({
        mutationFn: async ({ option, value }) => {
            if (!userId) {
                throw new Error('Cannot set social preference when logged out');
            }
            await setSocialPreference(userId, option, value);
        },
        onMutate: async ({ option, value }) => {
            const key = socialPreferencesKeys.byUser(userId);
            await queryClient.cancelQueries({ queryKey: key });
            const previous = queryClient.getQueryData<SocialPreferencesVo>(key);
            if (previous) {
                queryClient.setQueryData<SocialPreferencesVo>(key, { ...previous, [option]: value });
            }
            return { previous };
        },
        onError: (_err, _vars, context) => {
            if (context?.previous) {
                queryClient.setQueryData(socialPreferencesKeys.byUser(userId), context.previous);
            }
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: socialPreferencesKeys.all });
            void queryClient.invalidateQueries({ queryKey: socialActivityKeys.all });
            void queryClient.invalidateQueries({ queryKey: socialProfileMetricsKeys.all });
            void queryClient.invalidateQueries({ queryKey: socialProfileHoldingsKeys.all });
            void queryClient.invalidateQueries({ queryKey: socialCategoryPnlKeys.all });
        },
    });
}
